import React, { memo, useState } from 'react'
import { Box } from 'transfix-ui/core'
import { $enum } from 'ts-enum-util'
import { useRefresh } from 'utils/hooks/useRefresh'
import * as yup from 'yup'
import { ContactAutosuggest } from 'apps/fleet-planner/components/atoms/ContactAutosuggest'
import { CUSTOMER_CONTACT_PURPOSE, ICustomerContact } from 'apps/fleet-planner/types'
import { Button } from 'transfix-ui'

interface ISubscribersAddSearchBarProps {
  onAddSubscriber: (newSubscriber: ICustomerContact) => void
}

export const SubscriberSchema = yup
  .object<ICustomerContact>({
    name: yup.string().nullable(),
    purpose: yup
      .mixed()
      .oneOf([...$enum(CUSTOMER_CONTACT_PURPOSE).getValues()])
      .default(CUSTOMER_CONTACT_PURPOSE.SUBSCRIBER),
    email: yup.string().email().required(),
  })
  .required()

const ERROR_MESSAGE = 'Please enter a valid email'

export const SubscribersAddSearchBar = memo<ISubscribersAddSearchBarProps>(
  ({ onAddSubscriber }) => {
    const [inputValue, setInputValue] = useState<string>('')
    const [error, setError] = useState<string>('')
    // Reset the autocomplete input when customer updates
    const { key: autosuggestKey, resetKey } = useRefresh()

    /**
     *
     * @param val the input value coming from ContactAutosuggest component
     * The reason we update 2 input states is to keep the separate components modular
     */
    const inputChangeHandler = (val: string = '') => {
      setInputValue(val)
    }

    /**
     * When the user selects an existing contact from the autosuggest
     * @param option Selected contact option
     */
    const selectSubscriberHandler = async (newSubscriber?: Partial<ICustomerContact>) => {
      if (newSubscriber) {
        const isValidSubscriber = await SubscriberSchema.isValid(newSubscriber)
        if (!isValidSubscriber) {
          setError(ERROR_MESSAGE)
        } else {
          setError('')
          resetKey()
          onAddSubscriber(newSubscriber as ICustomerContact)
        }
      }
    }

    /**
     * When the user clicks the Add button to manually add a new subscriber email
     */
    const addSubscriberHandler = async () => {
      const subscriber = {
        name: null,
        email: inputValue,
        purpose: CUSTOMER_CONTACT_PURPOSE.SUBSCRIBER,
      }
      // Validate subscriber against schema
      const isValidSubscriber = await SubscriberSchema.isValid(subscriber)
      if (!isValidSubscriber) {
        setError(ERROR_MESSAGE)
      } else {
        setError('')
        resetKey()
        setInputValue('')
        onAddSubscriber(subscriber)
      }
    }

    return (
      <Box display='flex' pb={2}>
        <Box pr={2} flexGrow={10}>
          {/* Modular autosuggest component */}
          <ContactAutosuggest
            value={undefined}
            autosuggestKey={autosuggestKey}
            error={error}
            onInputChange={inputChangeHandler}
            onChange={selectSubscriberHandler}
          />
        </Box>
        <Box>
          <Button color='primary' onClick={addSubscriberHandler} size='large'>
            Add
          </Button>
        </Box>
      </Box>
    )
  }
)
